import { useState, useEffect } from 'react';
import { authFetch } from '@/utils/authFetch';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckSquare, Clock, CheckCircle2, FileText, FolderOpen, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, userRes] = await Promise.all([
          authFetch(`${BACKEND_URL}/api/dashboard/stats`),
          authFetch(`${BACKEND_URL}/api/auth/me`)
        ]);

        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }
        
        if (userRes.ok) {
          const userData = await userRes.json();
          setUser(userData);
        }
      } catch (error) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  const statCards = [
    {
      title: 'Total Tasks',
      value: stats?.total_tasks || 0,
      icon: CheckSquare,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      testId: 'stat-total-tasks'
    },
    {
      title: 'Projects',
      value: stats?.total_projects || 0,
      icon: CheckSquare,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      testId: 'stat-total-projects'
    },
    {
      title: 'Clients',
      value: stats?.total_clients || 0,
      icon: CheckSquare,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      testId: 'stat-total-clients'
    },
    {
      title: 'Meetings',
      value: stats?.total_meetings || 0,
      icon: CheckSquare,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      testId: 'stat-total-meetings'
    },
    {
      title: 'Pending',
      value: stats?.pending_tasks || 0,
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      testId: 'stat-pending-tasks'
    },
    {
      title: 'In Progress',
      value: stats?.in_progress_tasks || 0,
      icon: CheckSquare,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      testId: 'stat-inprogress-tasks'
    },
    {
      title: 'Completed',
      value: stats?.completed_tasks || 0,
      icon: CheckCircle2,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      testId: 'stat-completed-tasks'
    },
    {
      title: 'Total Reports',
      value: stats?.total_reports || 0,
      icon: FileText,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
      testId: 'stat-total-reports'
    },
    {
      title: 'Documents',
      value: stats?.total_documents || 0,
      icon: FolderOpen,
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
      testId: 'stat-total-documents'
    }
  ];

  if (user?.role === 'admin') {
    statCards.push({
      title: 'Employees',
      value: stats?.total_employees || 0,
      icon: Users,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      testId: 'stat-total-employees'
    });
    statCards.push({
      title: 'Team Members',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      testId: 'stat-total-users'
    });
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-container">
        {/* Welcome Section */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome back, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-slate-600">Here's what's happening with your work today.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title} className="card-hover" data-testid={stat.testId}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">
                    {stat.title}
                  </CardTitle>
                  <div className={`${stat.bgColor} p-2 rounded-md`}>
                    <Icon className={`w-5 h-5 ${stat.color}`} strokeWidth={2} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <a
                href="/tasks"
                data-testid="quick-action-create-task"
                className="flex items-center p-4 bg-indigo-50 border border-indigo-100 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                <CheckSquare className="w-6 h-6 text-indigo-600 mr-3" />
                <div>
                  <p className="font-medium text-slate-900">Manage Tasks</p>
                  <p className="text-sm text-slate-600">View and create tasks</p>
                </div>
              </a>
              
              <a
                href="/reports"
                data-testid="quick-action-submit-report"
                className="flex items-center p-4 bg-violet-50 border border-violet-100 rounded-lg hover:bg-violet-100 transition-colors"
              >
                <FileText className="w-6 h-6 text-violet-600 mr-3" />
                <div>
                  <p className="font-medium text-slate-900">Submit Report</p>
                  <p className="text-sm text-slate-600">Daily work reports</p>
                </div>
              </a>
              
              <a
                href="/documents"
                data-testid="quick-action-view-documents"
                className="flex items-center p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <FolderOpen className="w-6 h-6 text-slate-600 mr-3" />
                <div>
                  <p className="font-medium text-slate-900">Documents</p>
                  <p className="text-sm text-slate-600">Company files</p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}